/**
 * HLOS-161 — TS-side wrapper for the Apple Foundation Models runtime.
 *
 * The Swift `AppleLLMRuntimeBridge` (installed as a `WKScriptMessageHandler`
 * on the Capacitor WKWebView) exposes three async methods to the page
 * via `window.webkit.messageHandlers.hlos`. This module wraps that
 * asynchronous surface in a `Promise<…>`-shaped object that the Pyodide
 * Python kernel can call through the standard
 * `pyodide.globals.set("apple_llm_runtime", …)` bridge.
 *
 * Design constraints (HLOS-160 + HLOS-161):
 * - **No PII** crosses the JS↔Swift boundary other than what the Python
 *   `AppleLocalProvider._capture_payload` already filtered through
 *   ``LLM_ALLOWED_FIELDS`` (CL-07/08).
 * - **No infrastructure names** in outbound messages (Princípio XI):
 *   the only top-level keys are `id`, `method`, and the per-method
 *   payload (e.g. `modelId`, `prompt`, `maxTokens`).
 * - **Bounded wait** on Swift responses: each call has a default
 *   `DEFAULT_TIMEOUT_MS = 2_000` (matches the AC p95 target). If Swift
 *   does not answer in time, the Promise rejects with a clear
 *   `Timeout` Error so the upstream R3 silent fallback can run.
 *
 * This module is **iOS-only**. On web and Android, the helpers refuse
 * to attach a runtime so that the upstream `WEBLLM_RUNTIME` env can
 * be the single source of truth for provider selection.
 *
 * HLOS-168-B.12: usa `hlosRouter.ts` para receber responses, em vez de
 * instalar o próprio global `__hlos_handleMessage__` (causava colisão
 * com vault_bridge.ts).
 */
import { Capacitor } from "@capacitor/core";
import { installHlosRouterOnce, registerLLMHandler } from "./hlosRouter";

/**
 * Default response budget per call. Matches HLOS-161 AC-1 (<2s p95).
 * HLOS-323: Aumentado para 30s — Vision Analysis (Apple Vision) + LLM
 * generation (Foundation Models) pode demorar mais que 2s.
 */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Underlying iOS message shape posted to `window.webkit.messageHandlers.hlos`. */
type AppleLLMMessage = {
  id: string;
  // HLOS-168-B.24: snake_case para casar com o Python-side AppleLocalProvider
  method: "load_model" | "generate" | "is_loaded";
} & Record<string, unknown>;

/** Response shape returned by Swift. */
type AppleLLMResponse = {
  id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
};

/** Public surface exposed to the Pyodide Python kernel.
 *
 * HLOS-168-B.24: snake_case naming convention to match Python-side
 * AppleLocalProvider methods. Pyodide does NOT auto-convert
 * snake_case ↔ camelCase, so we need to use snake_case directly.
 */
export interface AppleLLMRuntime {
  load_model(model_id: string): Promise<number>;
  generate(prompt: string, max_tokens: number): Promise<string>;
  is_loaded(): Promise<boolean>;
}

/** Minimal subset of the Pyodide surface that the bridge uses. */
interface PyodideLike {
  globals: {
    set(key: string, value: unknown): void;
    get(key: string): unknown;
  };
  runPythonAsync(code: string): Promise<unknown>;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  method: AppleLLMMessage["method"];
}

interface IOSBridge {
  postMessage: (msg: AppleLLMMessage) => void;
}

/**
 * True iff the page is hosted in an iOS Capacitor WKWebView with our
 * `WKScriptMessageHandler` registered under `window.webkit.messageHandlers.hlos`.
 */
export function isAppleLLMRuntimeAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const cap = window as unknown as { webkit?: { messageHandlers?: { hlos?: unknown } } };
  if (!cap.webkit?.messageHandlers?.hlos) return false;
  // Defense in depth — only attach on iOS Capacitor, never on a webview
  // exposed through the desktop Safari (would be a different setup).
  if (Capacitor.getPlatform() !== "ios") return false;
  if (!Capacitor.isNativePlatform()) return false;
  return true;
}

/**
 * Inject the `apple_llm_runtime` global into Pyodide so the Python
 * `AppleLocalProvider` can call `load_model_async()`, `generate_async()`,
 * and `is_loaded()` against the iOS Foundation Models runtime.
 *
 * Returns `true` on success. On non-iOS platforms or when the
 * WKScriptMessageHandler is missing, returns `false` and attaches
 * nothing — the upstream fallback path (mock / templates) takes over.
 *
 * Idempotent: calling twice on the same Pyodide instance does not
 * double-attach handlers.
 */
export function injectAppleLLMRuntime(pyodide: PyodideLike): boolean {
  if (!isAppleLLMRuntimeAvailable()) return false;
  if (pyodide.globals.get("apple_llm_runtime")) return true; // already injected

  const handler = getIOSBridge();
  if (!handler) return false;

  const pending = new Map<string, PendingRequest>();
  let counter = 0;
  const nextId = (): string => {
    counter += 1;
    return `hlm_${Date.now()}_${counter}`;
  };

  // HLOS-168-B.12: registrar handler no router compartilhado
  // (hlosRouter.ts) em vez de instalar global próprio (colidia com vault_bridge).
  installHlosRouterOnce();

  const call = <T>(method: AppleLLMMessage["method"], payload: Record<string, unknown>): Promise<T> => {
    const id = nextId();
    console.info("[HLOS][LLM-bridge] call:", method, "id=", id);
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id);
        console.warn("[HLOS][LLM-bridge] TIMEOUT:", method, "id=", id);
        reject(new Timeout(method));
      }, DEFAULT_TIMEOUT_MS);
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer, method });
      // Registrar handler no router (auto-cleanup após 1 chamada).
      registerLLMHandler(id, (raw: unknown) => {
        console.info("[HLOS][LLM-bridge] response:", method, "id=", id, "raw=", JSON.stringify(raw).slice(0, 100));
        if (!raw || typeof raw !== "object") {
          console.warn("[HLOS][LLM-bridge] response not object:", method);
          return;
        }
        const response = raw as AppleLLMResponse;
        const p = pending.get(response.id);
        if (!p) return;
        pending.delete(response.id);
        clearTimeout(p.timer);
        if (response.ok) {
          console.info("[HLOS][LLM-bridge] RESOLVED:", method, "result=", JSON.stringify(response.result).slice(0, 100));
          p.resolve(response.result);
        } else {
          console.warn("[HLOS][LLM-bridge] REJECTED:", method, "error=", response.error);
          p.reject(new Error(response.error ?? "AppleLLMRuntime Swift error"));
        }
      });
      handler.postMessage({ id, method, ...payload });
    });
  };

  const runtime: AppleLLMRuntime = {
    // HLOS-168-B.24: snake_case method names (consistente com AppleLocalProvider Python-side
    // que chama runtime.load_model / runtime.is_loaded / runtime.generate)
    load_model: (modelId) => call<number>("load_model", { modelId }),
    generate: (prompt, maxTokens) => call<string>("generate", { prompt, maxTokens }),
    is_loaded: () => call<boolean>("is_loaded", {}),
  };

  // HLOS-168-B.23: usar globalThis explicitamente (mais robusto que pyodide.globals.set)
  // Em alguns builds Pyodide, pyodide.globals.set não é equivalente a definir
  // window.apple_llm_runtime. globalThis é o caminho mais seguro.
  (globalThis as unknown as Record<string, unknown>).apple_llm_runtime = runtime;
  // Tentar também via pyodide.globals para retrocompatibilidade
  try {
    pyodide.globals.set("apple_llm_runtime", runtime);
  } catch {
    // Fallback silencioso — globalThis já foi setado acima
  }
  return true;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

class Timeout extends Error {
  constructor(method: string) {
    super(`AppleLLMRuntime.${method}() timed out after ${DEFAULT_TIMEOUT_MS}ms`);
    this.name = "AppleLLMRuntimeTimeout";
  }
}

function getIOSBridge(): IOSBridge | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    webkit?: { messageHandlers?: { hlos?: IOSBridge } };
  };
  const h = w.webkit?.messageHandlers?.hlos;
  if (!h || typeof h.postMessage !== "function") return null;
  return h;
}

// HLOS-168-B.12: installResponseListener removido — handler global agora
// é gerenciado por `hlosRouter.ts`. Mantemos só `registerLLMHandler`
// chamado em `call()` para despachar responses por id.
